import { useEffect } from 'react';
import { useMobileDetect } from '@/hooks/useMobileDetect';

/**
 * Componente que aplica otimizações automáticas para mobile
 * Deve ser incluído no App.js
 */
export const MobileOptimizations = () => {
  const { isMobile, isIOS, isAndroid, hasTouch, orientation } = useMobileDetect();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    // 1. Ajuste do viewport height para mobile (problema do 100vh)
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--real-vh', `${window.innerHeight}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);

    // 2. Prevenir bounce scroll no iOS
    if (isIOS) {
      document.body.style.overscrollBehavior = 'none';
    }

    // 3. Ajustar tamanho de fonte para inputs (prevenir zoom no iOS)
    if (isMobile) {
      const style = document.createElement('style');
      style.id = 'mobile-optimizations';
      style.textContent = `
        /* Prevenir zoom em inputs no iOS */
        input[type="text"],
        input[type="email"],
        input[type="password"],
        input[type="number"],
        input[type="tel"],
        input[type="search"],
        select,
        textarea {
          font-size: 16px !important;
        }

        /* Touch targets mínimos de 44px */
        .is-mobile button,
        .is-mobile a,
        .is-mobile [role="button"],
        .is-mobile input[type="checkbox"],
        .is-mobile input[type="radio"] {
          min-height: 44px;
          min-width: 44px;
        }

        /* Remover delay de tap no mobile */
        .is-touch * {
          touch-action: manipulation;
        }

        /* Usar altura real do viewport */
        .mobile-full-height {
          height: 100vh;
          height: calc(var(--vh, 1vh) * 100);
        }

        /* Safe area insets para notch */
        .safe-area-top {
          padding-top: env(safe-area-inset-top, 0);
        }

        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }

        .safe-area-left {
          padding-left: env(safe-area-inset-left, 0);
        }

        .safe-area-right {
          padding-right: env(safe-area-inset-right, 0);
        }

        .safe-area-all {
          padding: env(safe-area-inset-top, 0) 
                   env(safe-area-inset-right, 0) 
                   env(safe-area-inset-bottom, 0) 
                   env(safe-area-inset-left, 0);
        }

        /* Espaçamento para bottom navigation */
        .is-mobile main {
          padding-bottom: 80px;
        }

        /* Scrollbar fina em mobile */
        .is-mobile ::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }

        /* Desabilitar seleção de texto em elementos interativos */
        .is-touch button,
        .is-touch a,
        .is-touch [role="button"] {
          -webkit-user-select: none;
          user-select: none;
        }

        /* Feedback visual melhor para touch */
        .is-touch button:active,
        .is-touch a:active,
        .is-touch [role="button"]:active {
          opacity: 0.7;
          transform: scale(0.98);
        }

        /* Cards responsivos */
        .is-mobile .property-card,
        .is-mobile .card {
          margin-left: 0;
          margin-right: 0;
        }

        /* Modal fullscreen em mobile */
        .is-mobile [role="dialog"] {
          max-width: 100vw !important;
          max-height: calc(var(--vh, 1vh) * 100) !important;
          margin: 0 !important;
          border-radius: 0 !important;
        }

        /* Landscape mode adjustments */
        .is-landscape.is-mobile main {
          padding-bottom: 60px;
        }

        /* Otimizações de performance */
        .is-mobile img {
          content-visibility: auto;
        }

        .is-mobile .will-change-transform {
          will-change: transform;
        }
      `;
      
      // Remove estilo antigo se existir
      const existingStyle = document.getElementById('mobile-optimizations');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      document.head.appendChild(style);
    }

    // 4. Desabilitar pull-to-refresh nativo em PWA
    if (isMobile) {
      document.body.style.overscrollBehaviorY = 'contain';
    }

    // 5. Prevenir zoom com double-tap
    if (hasTouch) {
      let lastTouchEnd = 0;
      const preventZoom = (e) => {
        const now = Date.now();
        if (now - lastTouchEnd <= 300) {
          e.preventDefault();
        }
        lastTouchEnd = now;
      };
      document.addEventListener('touchend', preventZoom, { passive: false });

      return () => {
        document.removeEventListener('touchend', preventZoom);
      };
    }

    return () => {
      window.removeEventListener('resize', setVH);
      const style = document.getElementById('mobile-optimizations');
      if (style) style.remove();
    };
  }, [isMobile, isIOS, isAndroid, hasTouch, orientation]);

  return null; // Este componente não renderiza nada
};

export default MobileOptimizations;
